// CR075-P5: Retry Step Dialog - Retry failed merchants or select specific ones
import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { RefreshCw, Loader2, AlertCircle, CheckCircle2, XCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface RetryStepDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  batchRunId: string;
  stepKey: string;
  stepName: string;
  onRetryStarted: () => void;
}

interface UnitAttempt {
  id: string;
  merchant_source_id: string | null;
  status: string;
  error_message: string | null;
  attempt_no: number;
  records_out: number | null;
}

export function RetryStepDialog({
  open,
  onOpenChange,
  batchRunId,
  stepKey,
  stepName,
  onRetryStarted,
}: RetryStepDialogProps) {
  const { toast } = useToast();
  const [retryMode, setRetryMode] = useState<"FAILED_ONLY" | "SELECTED" | "FULL">("FAILED_ONLY");
  const [selectedUnits, setSelectedUnits] = useState<Set<string>>(new Set());
  const [units, setUnits] = useState<UnitAttempt[]>([]);
  const [loading, setLoading] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);

  const failedUnits = units.filter((u) => u.status === "FAILED" || u.status === "TIMED_OUT");
  const succeededUnits = units.filter((u) => u.status === "COMPLETE");

  // Fetch unit attempts
  useEffect(() => {
    const fetchUnits = async () => {
      if (!open) return;
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from("cr075_batch_run_step_attempts")
          .select("*")
          .eq("batch_run_id", batchRunId)
          .eq("step_key", stepKey)
          .order("created_at", { ascending: false });

        if (error) throw error;

        // Dedupe by merchant_source_id (take latest attempt)
        const latestByMerchant = new Map<string, UnitAttempt>();
        for (const attempt of (data || []) as UnitAttempt[]) {
          const key = attempt.merchant_source_id || attempt.id;
          if (!latestByMerchant.has(key) || attempt.attempt_no > (latestByMerchant.get(key)?.attempt_no || 0)) {
            latestByMerchant.set(key, attempt);
          }
        }
        setUnits(Array.from(latestByMerchant.values()));
      } catch (err) {
        console.error("Failed to fetch unit attempts:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchUnits();
  }, [open, batchRunId, stepKey]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setRetryMode("FAILED_ONLY");
      setSelectedUnits(new Set());
      setUnits([]);
    }
  }, [open]);

  const toggleUnit = (unitId: string) => {
    const newSelected = new Set(selectedUnits);
    if (newSelected.has(unitId)) {
      newSelected.delete(unitId);
    } else {
      newSelected.add(unitId);
    }
    setSelectedUnits(newSelected);
  };

  const handleRetry = async () => {
    if (isRetrying) return;

    setIsRetrying(true);
    try {
      // Determine which units to retry
      let unitsToRetry: string[] = [];
      if (retryMode === "FAILED_ONLY") {
        unitsToRetry = failedUnits.map((u) => u.merchant_source_id || u.id).filter(Boolean) as string[];
      } else if (retryMode === "SELECTED") {
        unitsToRetry = Array.from(selectedUnits);
      }
      // FULL mode = empty array (retry all)

      // Call the step function with retry parameters
      const functionName = stepKey === "S2" ? "batch-run-website-collect" : `batch-run-${stepKey.toLowerCase()}-collect`;
      
      const { data, error } = await supabase.functions.invoke(functionName, {
        body: {
          batch_run_id: batchRunId,
          retry_mode: retryMode,
          retry_unit_ids: retryMode !== "FULL" ? unitsToRetry : undefined,
        },
      });

      if (error) throw error;

      toast({
        title: "Retry started",
        description: `Retrying ${retryMode === "FULL" ? "all" : unitsToRetry.length} units for ${stepName}`,
      });

      onOpenChange(false);
      onRetryStarted();
    } catch (err) {
      console.error("Failed to start retry:", err);
      toast({
        title: "Error",
        description: err instanceof Error ? err.message : "Failed to start retry",
        variant: "destructive",
      });
    } finally {
      setIsRetrying(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "COMPLETE":
        return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case "FAILED":
      case "TIMED_OUT":
        return <XCircle className="h-4 w-4 text-destructive" />;
      default:
        return <AlertCircle className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const canRetry = retryMode === "FULL" || 
    (retryMode === "FAILED_ONLY" && failedUnits.length > 0) ||
    (retryMode === "SELECTED" && selectedUnits.size > 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5" />
            Retry {stepName}
          </DialogTitle>
          <DialogDescription>
            Select which units to retry for step {stepKey}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : units.length === 0 ? (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>No attempts found</AlertTitle>
            <AlertDescription>
              This step hasn't been run yet. Use "Run" instead of "Retry".
            </AlertDescription>
          </Alert>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-4 text-sm">
              <Badge variant="outline" className="gap-1">
                <CheckCircle2 className="h-3 w-3 text-green-500" />
                {succeededUnits.length} succeeded
              </Badge>
              <Badge variant="destructive" className="gap-1">
                <XCircle className="h-3 w-3" />
                {failedUnits.length} failed
              </Badge>
            </div>

            <RadioGroup value={retryMode} onValueChange={(v) => setRetryMode(v as typeof retryMode)}>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="FAILED_ONLY" id="failed-only" />
                <Label htmlFor="failed-only">Retry failed only ({failedUnits.length})</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="SELECTED" id="selected" />
                <Label htmlFor="selected">Select specific units</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="FULL" id="full" />
                <Label htmlFor="full">Retry all ({units.length})</Label>
              </div>
            </RadioGroup>

            {retryMode === "SELECTED" && (
              <ScrollArea className="h-48 rounded-md border p-2">
                <div className="space-y-2">
                  {units.map((unit) => (
                    <div
                      key={unit.id}
                      className="flex items-center space-x-2 p-2 rounded hover:bg-muted/50"
                    >
                      <Checkbox
                        id={unit.id}
                        checked={selectedUnits.has(unit.merchant_source_id || unit.id)}
                        onCheckedChange={() => toggleUnit(unit.merchant_source_id || unit.id)}
                      />
                      <Label htmlFor={unit.id} className="flex-1 flex items-center gap-2 cursor-pointer">
                        {getStatusIcon(unit.status)}
                        <span className="font-medium">{unit.merchant_source_id?.slice(0, 8) || unit.id.slice(0, 8)}</span>
                        {(unit.records_out ?? 0) > 0 && (
                          <span className="text-xs text-muted-foreground">({unit.records_out} records)</span>
                        )}
                      </Label>
                      {unit.error_message && (
                        <span className="text-xs text-destructive truncate max-w-[150px]" title={unit.error_message}>
                          {unit.error_message}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isRetrying}>
            Cancel
          </Button>
          <Button onClick={handleRetry} disabled={!canRetry || isRetrying || units.length === 0}>
            {isRetrying ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Retrying...
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4 mr-2" />
                Retry{" "}
                {retryMode === "FULL"
                  ? "All"
                  : retryMode === "FAILED_ONLY"
                  ? `${failedUnits.length} Failed`
                  : `${selectedUnits.size} Selected`}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
